import { Fragment, PropsWithChildren, useEffect, useMemo, useState } from 'react';
import { Box, List, ListItem, ListItemButton, Typography } from '@mui/material';
import { Link, useParams } from 'react-router-dom';
import { useInView } from 'react-intersection-observer';
import { LoadingBox } from 'components/loading.box';
import { ListSkeleton } from 'components/skeletons';
import { EmptyState } from 'components/empty.state';
import { PersonSearchOutlined, TaskAltOutlined, TouchAppOutlined } from '@mui/icons-material';
import { format } from "date-fns";
import { fr } from 'date-fns/locale';
import { useGetTempWorkers } from 'core/temp_workers/hooks';
import { ListCard } from 'components/list/list.card';
import SearchFilters from 'components/SearchFilters';
import { PageHeader, SearchField } from 'components/layout/PageHeader';

const menuSidebarWidth: string = "24rem";

interface RetoursCandidatListProps {
    search: string;
    filters: { qualification?: string[] };
}

const RetoursCandidatList = ({ search, filters }: RetoursCandidatListProps) => {
    const { ref, inView } = useInView();
    const { candidateId } = useParams();

    const params = useMemo(() => {
        let base_params: any = { retours: true, archived: false };
        if (search) base_params = { ...base_params, search };
        if (filters.qualification) base_params = { ...base_params, qualification: filters.qualification };
        return base_params;
    }, [search, filters]);

    const { data, status, fetchNextPage, hasNextPage, isFetchingNextPage } = useGetTempWorkers(params);
    const pages = useMemo(() => data?.pages ?? [], [data]);

    useEffect(() => {
        if (inView && hasNextPage) fetchNextPage();
    }, [inView, hasNextPage, fetchNextPage]);

    if (status === 'loading') return <ListSkeleton />;
    if (status === 'error') return <Typography variant='subtitle1' color='error'>Une erreur est survenue</Typography>;

    const isEmpty = pages.every((group) => (group.temp_workers || []).length === 0);
    if (isEmpty) {
        if (search || (filters.qualification?.length ?? 0) > 0) {
            return <EmptyState icon={<PersonSearchOutlined />} title='Aucun résultat' description={search ? <>Aucun retour candidat ne correspond à « {search} ».</> : 'Aucun retour candidat ne correspond aux filtres sélectionnés.'} />;
        }
        return <EmptyState icon={<TaskAltOutlined />} title='Tout est à jour' description="Aucun candidat n'a modifié son profil depuis sa dernière synchronisation." />;
    }

    return (
        <Fragment>
            {pages.map((group, i) => (
                <Fragment key={i}>
                    {(group.temp_workers || []).map((temp_worker: any) =>
                        <ListCard
                            key={temp_worker.id}
                            title={temp_worker.full_name}
                            subtitle={temp_worker.synced_at !== null ? `Synchonisé le ${format(Date.parse(temp_worker.synced_at), 'dd/MM/yy', { locale: fr })}` : `Créé le ${format(Date.parse(temp_worker.created_at), 'dd/MM/yy', { locale: fr })}`}
                            avatarUrl={temp_worker.avatar_url}
                            avatarSubstitute={`${temp_worker.full_name[0].toUpperCase()}`}
                            selected={candidateId === temp_worker.id.toString()}
                            component={Link}
                            to={`/dashboard/retours-candidats/${temp_worker.id}`}
                            chips={[
                                temp_worker.available_at && { label: 'Disponible', color: 'success' },
                                temp_worker.in_mission && { label: 'En mission', color: 'info' },
                            ].filter((item) => item)}
                        />
                    )}
                </Fragment>
            ))}
            {hasNextPage && (
                <ListItem>
                    <ListItemButton onClick={() => fetchNextPage()} ref={ref} disabled={isFetchingNextPage}>
                        {isFetchingNextPage ? <LoadingBox /> : 'Charger la suite'}
                    </ListItemButton>
                </ListItem>
            )}
        </Fragment>
    );
};

const IndexPage = ({ children }: PropsWithChildren) => {
    const [search, setSearch] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [filters, setFilters] = useState<{ qualification?: string[] }>({});

    useEffect(() => {
        const delayDebounceFn = setTimeout(() => setSearch(searchTerm), 500);
        return () => clearTimeout(delayDebounceFn);
    }, [searchTerm]);

    const hasFilters = filters.qualification && filters.qualification.length > 0;

    return (
        <Fragment>
            <PageHeader title='Retours candidats' subtitle='Candidats ayant modifié leur profil'>
                <SearchField
                    value={searchTerm}
                    onChange={setSearchTerm}
                    placeholder='Rechercher un candidat'
                    trailing={<SearchFilters onFiltersUpdate={(newFilters) => setFilters(newFilters)} currentFiltersCount={hasFilters ? 1 : 0} />}
                />
            </PageHeader>
            <Box sx={{ display: 'flex', gap: 0, justifyContent: 'space-between' }}>
                <List sx={{ width: menuSidebarWidth, flexShrink: 0, p: '1rem', height: 'calc(100vh - 72px)', overflowY: 'scroll' }} disablePadding>
                    <RetoursCandidatList search={search} filters={filters} />
                </List>
                <Box sx={{ flexGrow: 1, flexShrink: 1, minWidth: 0, pr: '1rem', py: '1rem', height: 'calc(100vh - 72px)', overflowY: 'scroll' }}>
                    {children ? (
                        <Box sx={{ borderRadius: '1rem', border: '1px solid #EDF0F3', boxShadow: 1, backgroundColor: 'background.paper', minWidth: 0, overflow: 'hidden' }}>
                            {children}
                        </Box>
                    ) : (
                        <EmptyState
                            size='md'
                            variant='dashed'
                            icon={<TouchAppOutlined />}
                            title="Sélectionnez un candidat"
                            description="Cliquez sur un candidat pour vérifier les modifications de son profil."
                            sx={{ minHeight: 'calc(100vh - 72px - 2rem)' }}
                        />
                    )}
                </Box>
            </Box>
        </Fragment>
    );
};

export { IndexPage };
